// Home.jsx
import React from 'react';
import ReactMarkdown from "react-markdown"; // markdown 渲染
import {
  Layers,
  Users,
  ArrowRight,
  Sparkles,
  // User,  // 未使用的图标；若需使用可取消注释
  FileText,
  GraduationCap,
  Newspaper,
  Rocket
} from 'lucide-react';

import { getText } from '../data/content';
// import { getTextColor } from '../theme/theme';  // 未使用的工具函数；若需使用可取消注释
import { theme, combineClasses, getCardStyle, getButtonStyle } from '../theme/theme';

import NewsCarousel from '../components/NewsCarousel';
import TeamCarousel from '../components/TeamCarousel';
import Collapsible from '../components/Collapsible';

/**
 * ============================================================================
 * 页面：首页 - Home.jsx
 * ============================================================================
 *
 * 【作用】首页主视觉：新闻轮播、主标题/副标题、行动按钮（研究方法/团队）、核心价值区块、快速导航卡片（研究/论文/新闻）。
 * 【如何修改】文案来自 content（hero 等），改 src/data/content-home.jsx；样式类名来自 theme，改 src/theme/theme.js。
 *
 * 【参数说明】组件接收 props：
 * - onChangePage: 函数，点击导航按钮时调用，参数为页面键名（如 "research"、"team"、"news"）
 * - lang: "en" | "cn"，当前语言
 * - themeMode: "light" | "dark"，当前主题，用于切换亮/暗色样式
 *
 * 【新增说明｜布局与可访问性】（第三人称客观注释）
 * - 首屏布局采用“上下更均衡”的策略：在大屏使用 justify-center 让内容更居中，避免顶部拥挤与底部留白不均。
 * - 交互元素补充 aria-label：用于屏幕阅读器与可访问性工具识别按钮含义。
 * - 快速导航卡片使用更语义化的图标：提升信息密度与扫描效率。
 * - TeamCarousel 点击回调显式包装：避免“成员对象被误当作页面 key”导致跳转异常。
 *
 * 【新增说明｜样式与 className 参数释义】（第三人称客观注释）
 * - min-h-[90vh]：首屏最小高度为视口高度 90%，用于保证“主视觉”存在感。
 * - flex flex-col：使用 Flex 布局并纵向排列子元素，便于在不同屏幕上堆叠内容。
 * - text-center：文字居中对齐，强化主视觉信息聚焦。
 * - px-4：左右内边距（padding-left/right）为 1rem，避免内容贴边。
 * - pt-6 / md:pt-8：顶部内边距；md: 表示中等屏及以上（Tailwind 断点）使用更大间距。
 * - justify-start / md:justify-center：小屏顶部开始排列，大屏垂直居中，减少“上贴/下空”。
 * - max-w-*：限制内容最大宽度，避免超宽屏长行降低可读性。
 * - mx-auto：水平居中容器。
 * - mb-* / mt-*：上下外边距（margin），用于控制段落间距。
 * - leading-*：行高；tracking-*：字距；font-*：字重；用于标题的视觉层级。
 * - bg-clip-text + text-transparent + bg-gradient-to-r：渐变文字效果（通过背景裁剪实现）。
 * - group + group-hover:*：父元素 hover 触发子元素动画/样式变化。
 * - hover:-translate-y-2 + transition-all + duration-300：悬停上移与过渡动画，增强交互反馈。
 * - relative / absolute / z-10：用于背景装饰与内容层叠控制。
 */
const HomeView = ({ onChangePage, lang, themeMode = "dark" }) => {
  const isLight = themeMode === "light";

  return (
    <div className={theme.animations.fadeIn}>
      {/* ############################################################################
          ############## 01. HERO SECTION（主视觉） #################################
          ############################################################################ */}
      {/* Hero Section - 保持原有设计 */}
      <section
        className={combineClasses(
          // relative：允许内部 absolute 元素（装饰/背景）基于该容器定位
          // min-h-[90vh]：首屏高度基准，保证大屏不显得内容过少
          // flex flex-col：纵向排列，移动端先堆叠后扩展
          // text-center：主视觉强调中心对齐
          // px-4：左右内边距，避免贴边
          "relative min-h-[90vh] flex flex-col text-center px-4",
          // [布局修正] 在中等屏及以上使用居中对齐，减少首屏“上贴/下空”的观感
          // pt-10 / md:pt-16：顶部留白；md 断点（>=768px）使用更大留白
          "pt-10 md:pt-16",
          // justify-start：小屏从上开始排列；md:justify-center：大屏垂直居中
          "justify-start"
          // "md:justify-center": 如果选择这个则首页会居中对齐，但是会导致首页的顶部留白变小，因为md:pt-20会覆盖pt-10
        )}
      >
        <div
          className={combineClasses(
            // max-w-5xl：限制主内容最大宽度
            // mx-auto：水平居中
            // w-full：占满可用宽度，配合 max-w-* 控制上限
            "max-w-5xl mx-auto w-full"
          )}
        >
          {/* 新闻轮播 */}
          <div
            className={combineClasses(
              // mb-8：与标题区拉开距离
              // max-w-2xl：限制轮播宽度，避免过宽影响信息聚焦
              // mx-auto：水平居中
              "mb-8 max-w-2xl mx-auto",
            )}
          >
            <NewsCarousel
              lang={lang}
              themeMode={themeMode}
              onNewsClick={() => onChangePage("news")}
            />
          </div>

          {/* 主标题 */}
          <h1
            className={combineClasses(
              // text-5xl / md:text-8xl：响应式字号；md 断点放大标题
              // font-black：超粗字重，强化品牌感
              // tracking-tighter：收紧字距，视觉更紧凑
              // mb-8：标题与副标题间距
              // leading-[1.1]：自定义行高，避免超大字时行距过大
              "text-5xl md:text-8xl font-black tracking-tighter mb-8 leading-[1.1]"
            )}
          >
            <span
              className={combineClasses(
                // isLight：主题色切换
                isLight ? "text-gray-900" : "text-white",
                // drop-shadow-2xl：提高标题在复杂背景下的对比度
                "drop-shadow-2xl"
              )}
            >
              {getText('home.hero.titlePrefix', lang)}
            </span>
            <br />
            <span
              className={combineClasses(
                // bg-clip-text + text-transparent：将背景裁剪为文字形状，实现渐变文字
                "bg-clip-text text-transparent bg-gradient-to-r",
                // theme.colors.gradients.primary：项目统一渐变
                theme.colors.gradients.primary,
                // theme.animations.gradientX：渐变动画（如左右流动）
                theme.animations.gradientX
              )}
            >
              {getText('home.hero.titleSuffix', lang)}
            </span>
          </h1>

          {/* 副标题 */}
          <p
            className={combineClasses(
              // mt-6：与标题留白
              // max-w-2xl：限制段落宽度，保持可读行长
              // mx-auto：水平居中
              // text-lg / md:text-xl：响应式字号
              // mb-12：与按钮区留白
              // leading-relaxed：更舒适行距
              // font-light：更轻字重，避免与标题竞争
              "mt-6 max-w-2xl mx-auto text-lg md:text-xl mb-12 leading-relaxed font-light",
              // [可读性修正] 浅色主题下正文颜色提升一级，避免偏淡
              isLight ? "text-gray-800" : "text-gray-400"
            )}
          >
            {getText('home.hero.subtitle', lang)}
          </p>

          {/* 行动按钮 */}
          <div
            className={combineClasses(
              // flex：按钮容器使用 flex
              // flex-col / sm:flex-row：小屏纵向堆叠，大屏横向排列
              // justify-center：水平居中
              // gap-5：按钮间距
              // mb-8：与下方内容留白
              "flex flex-col sm:flex-row justify-center gap-5 mb-8"
            )}
          >
            {/* 研究方法 */}
            <button
              onClick={() => onChangePage("research")}
              className={combineClasses(
                // getButtonStyle：项目统一按钮样式（含主题切换）
                getButtonStyle(themeMode, 'primary'),
                // group：用于内部图标 hover 动画
                "group"
              )}
            >
              <Layers className="w-5 h-5 group-hover:rotate-12 transition-transform" />
              {getText('home.hero.btnResearch', lang)}
            </button>


            {/* 团队 */}
            <button
              onClick={() => onChangePage("team")}
              className={getButtonStyle(themeMode, 'secondary')}
            >
              <Users className="w-5 h-5" />
              {getText('home.hero.btnTeam', lang)}
            </button>

            {/* 加入我们 */}
            <button
              onClick={() => onChangePage("openings")}
              className={getButtonStyle(themeMode, 'tertiary')}
            >
              <Rocket className="w-5 h-5" />
              {getText('home.hero.btnApplyNow', lang)}
            </button>
          </div>
        </div>
      </section>

      {/* ############################################################################
          ############## 02. MISSION SECTION（使命/愿景） ###########################
          ############################################################################ */}
      {/* 核心价值主张 - 新设计的部分 */}
      <section
        className={combineClasses(
          // py-12：上下内边距，形成清晰分区
          // px-4：左右内边距
          "py-12 px-4",
          // theme.layout.container：项目统一容器宽度与居中逻辑
          theme.layout.container
        )}
      >
        <div className="text-center mb-16">
          <h2
            className={combineClasses(
              // text-3xl / md:text-5xl：响应式标题字号
              // font-bold：强调区块标题
              // mb-6：标题与正文间距
              "text-3xl md:text-5xl font-bold mb-6",
              isLight ? "text-gray-900" : "text-white"
            )}
          >
            {getText('home.mission.title', lang)}
          </h2>

          <p
            className={combineClasses(
              // text-xl：正文更突出（使命段落通常比普通正文更大一号）
              // max-w-3xl：限制段落宽度，提升可读性
              // mx-auto：居中
              // leading-relaxed：舒适行距
              "text-xl max-w-5xl mx-auto leading-relaxed font-light",
              // [可读性修正] 浅色主题下正文颜色提升一级
              isLight ? "text-gray-800" : "text-gray-300"
            )}
          >
              {getText('home.mission.body', lang)}
          </p>

          {/* ############################################################################
              ############## 02-A. LOOP DIAGRAM（闭环示意图） #########################
              ############################################################################ */}
          {/* 
            [插图位置说明]（第三人称客观注释）
            - 插图置于 Mission 段落之后、优势模块之前，用于在“概念总述”后立刻提供结构化可视化解释。
            - 推荐使用 SVG 以保证清晰度与可主题化（light/dark 各一份），并使用懒加载减少首屏资源压力。
          */}
          <div
            className={combineClasses(
              // mt-8：与 Mission 正文拉开间距
              // flex justify-center：水平居中
              "mt-8 flex justify-center"
            )}
          >
            <img
              src={isLight ? getText('home.assets.loopDiagramLight', lang) : getText('home.assets.loopDiagramDark', lang)}
              alt={getText('home.assets.loopDiagramAlt', lang)}
              className={combineClasses(
                // w-full：在小屏占满可用宽度
                // max-w-3xl：限制最大宽度，避免超宽导致信息稀释
                "w-full max-w-3xl"
              )}
              loading="lazy"
            />
          </div>

          {/* 
            [可选说明｜Caption]（第三人称客观注释）
            - 若需要对图添加一句话注释，可在此处插入 <p className="...">caption</p>；
              建议文案简短（1 行），避免抢占主内容。
          */}
        </div>

        {/* ############################################################################
            ############## 03. ADVANTAGES + PILLARS（优势与研究环节） #################
            ############################################################################ */}
        {/* 核心优势展示：整块放入一个大下拉，格式不变 */}
        <Collapsible
          label={lang === 'cn' ? '研究环节与优势' : 'Research Pillars & Advantages'}
          labelClassName="text-xl"
          isLight={isLight}
          defaultOpen={false}
        >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start pt-2">
          {/* 左侧：核心理念 */}
          <div className="space-y-8">
            <div className="group">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-xl bg-gradient-to-r from-emerald-400 to-teal-600 text-white">
                  <Sparkles className="w-6 h-6" />
                </div>
                <h3
                  className={combineClasses(
                    "text-2xl font-bold",
                    isLight ? "text-gray-900" : "text-white"
                  )}
                >
                  <ReactMarkdown>
                    {getText('home.advantages.loop.title', lang)}
                  </ReactMarkdown>
                </h3>
              </div>

              {/* 
                [内容修正] 该段落对“合成—表征—理论—AI”的闭环做概括 + 关键细节，
                同时避免与 Hero subtitle 重复冗长。
              */}
              <p
                className={combineClasses(
                  // pl-16：为图标与标题区域留出一致缩进，使正文对齐更整齐
                  // leading-relaxed：可读行距
                  "leading-relaxed pl-16", // 自动换行以避免溢出
                  isLight ? "text-gray-800" : "text-gray-300"
                )}
              >
                <ReactMarkdown>
                  {getText('home.advantages.loop.body', lang)}
                </ReactMarkdown>
              </p>

              {/* 
                [术语说明｜缩写第一次出现] 
                - STM/STS: scanning tunneling microscopy/spectroscopy
                - ARPES: angle-resolved photoemission spectroscopy
                - MLFF: machine-learning force fields
                该说明为维护用途；若页面需要更“科普”，可将全称移至正文括号中显示。
              */}
            </div>

            <div className="group">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-xl bg-gradient-to-r from-cyan-400 to-blue-600 text-white">
                  <ArrowRight className="w-6 h-6" />
                </div>
                <h3
                  className={combineClasses(
                    "text-2xl font-bold",
                    isLight ? "text-gray-900" : "text-white"
                  )}
                >
                  {getText('home.advantages.fusion.title', lang)}
                </h3>
              </div>
              <p
                className={combineClasses(
                  "leading-relaxed pl-16",
                  isLight ? "text-gray-800" : "text-gray-300"
                )}
              >
                {getText('home.advantages.fusion.body', lang)}
              </p>
            </div>

            <div className="pt-6">
              <button
                onClick={() => onChangePage("research")}
                className={combineClasses(
                  // flex items-center gap-3：图标与文字对齐
                  // font-semibold：强调为“次级 CTA”
                  // text-lg：保持可点击性与可读性
                  // transition-colors：hover 平滑过渡
                  "group flex items-center gap-3 font-semibold text-lg transition-colors",
                  isLight ? "text-cyan-700 hover:text-gray-900" : "text-cyan-400 hover:text-white"
                )}
                aria-label={getText('home.a11y.learnMoreApproach', lang)}
              >
                {getText('home.advantages.learnMoreLabel', lang)}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* 右侧：统计数据或成就展示 */}
          <div
            className={combineClasses(
              // getCardStyle：项目统一卡片底色、边框/阴影等（随主题变化）
              getCardStyle(themeMode),
              // p-8：卡片内部留白
              // relative overflow-hidden：用于承载背景装饰（absolute）且避免溢出
              "p-8 relative overflow-hidden"
            )}
          >
            {/* 背景装饰 */}
            <div
              className={combineClasses(
                // absolute：脱离文档流作为背景装饰
                // top-0 right-0：定位到右上角
                // w-32 h-32：装饰尺寸
                // blur-3xl：模糊，制造柔和光晕
                // rounded-full：圆形
                "absolute top-0 right-0 w-32 h-32 blur-3xl rounded-full",
                isLight
                  ? "bg-gradient-to-br from-purple-500/10 to-cyan-500/10"
                  : "bg-gradient-to-br from-purple-500/20 to-cyan-500/20"
              )}
            />

            <div className="relative z-10">
              <h3
                className={combineClasses(
                  "text-2xl font-bold mb-8 text-center",
                  isLight ? "text-gray-900" : "text-white"
                )}
              >
                {getText('home.pillars.title', lang)}
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {/* Pillar 1 */}
                <div className="flex items-start gap-3">
                  {/* [对齐修正] 圆点使用 self-center，减少不同字体/行高导致的上下漂移 */}
                  <span className="self-center w-2.5 h-2.5 rounded-full bg-pink-400 flex-shrink-0" />
                  <div>
                    <div className={combineClasses("font-semibold text-lg", isLight ? "text-gray-900" : "text-white")}>
                      {getText('home.pillars.items.synthesis.title', lang)}
                    </div>
                    <div className={combineClasses("text-base", isLight ? "text-gray-700" : "text-gray-400")}>
                      {getText('home.pillars.items.synthesis.desc', lang)}
                    </div>
                  </div>
                </div>

                {/* Pillar 2 */}
                <div className="flex items-start gap-3">
                  <span className="self-center w-2.5 h-2.5 rounded-full bg-cyan-400 flex-shrink-0" />
                  <div>
                    <div className={combineClasses("font-semibold text-lg", isLight ? "text-gray-900" : "text-white")}>
                      {getText('home.pillars.items.characterization.title', lang)}
                    </div>
                    <div className={combineClasses("text-base", isLight ? "text-gray-700" : "text-gray-400")}>
                      {getText('home.pillars.items.characterization.desc', lang)}
                    </div>
                  </div>
                </div>

                {/* Pillar 3 */}
                <div className="flex items-start gap-3">
                  <span className="self-center w-2.5 h-2.5 rounded-full bg-purple-400 flex-shrink-0" />
                  <div>
                    <div className={combineClasses("font-semibold text-lg", isLight ? "text-gray-900" : "text-white")}>
                      {getText('home.pillars.items.theory.title', lang)}
                    </div>
                    <div className={combineClasses("text-base", isLight ? "text-gray-700" : "text-gray-400")}>
                      {getText('home.pillars.items.theory.desc', lang)}
                    </div>
                  </div>
                </div>

                {/* Pillar 4 */}
                <div className="flex items-start gap-3">
                  <span className="self-center w-2.5 h-2.5 rounded-full bg-emerald-400 flex-shrink-0" />
                  <div>
                    <div className={combineClasses("font-semibold text-lg", isLight ? "text-gray-900" : "text-white")}>
                      {getText('home.pillars.items.ai.title', lang)}
                    </div>
                    <div className={combineClasses("text-base", isLight ? "text-gray-700" : "text-gray-400")}>
                      {getText('home.pillars.items.ai.desc', lang)}
                    </div>
                  </div>
                </div>
              </div>

              {/* 
                [维护提示] 若未来需要展示“量化指标”（论文/项目/合作/设备等），可在此卡片内新增统计块，
                但应避免“过度承诺”式表述（例如 “redefining” / “breakthrough guaranteed” 等）。
              */}
            </div>
          </div>
        </div>
        </Collapsible>
      </section>

      {/* ############################################################################
          ############## 04. TEAM PREVIEW（团队预览） ###############################
          ############################################################################ */}
      {/* Team预览栏 - 在Our mission和Explore More之间 */}
      <section className={combineClasses(theme.layout.section, theme.layout.container)}>
        <div className="text-center mb-12">
          <h2
            className={combineClasses(
              "text-3xl md:text-5xl font-bold mb-4",
              isLight ? "text-gray-900" : "text-white"
            )}
          >
            {getText('team.title', lang)}
          </h2>
          <p
            className={combineClasses(
              "text-lg max-w-2xl mx-auto",
              isLight ? "text-gray-700" : "text-gray-400"
            )}
          >
            {getText('team.subtitle', lang)}
          </p>
        </div>

        {/* 团队成员轮播 - 随机显示并可滚动，已包括PI卡片 */}
        <TeamCarousel
          lang={lang}
          themeMode={themeMode}
          includePI // 新增属性，用于在轮播中同时包含PI卡（见TeamCarousel实现）
          onMemberClick={() => onChangePage("team")}
        />

        {/* 查看全部团队成员按钮 */}
        <div className="text-center">
          <button
            onClick={() => onChangePage("team")}
            className={combineClasses(
              getButtonStyle(themeMode, 'secondary'),
              "inline-flex items-center gap-2"
            )}
            aria-label={getText('home.a11y.viewAllTeam', lang)}
          >
            {getText('home.teamPreview.viewAllLabel', lang)}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* ############################################################################
          ############## 05. EXPLORE MORE（快速导航） ################################
          ############################################################################ */}
      {/* 快速导航 - 简化版 */}
      <section className={combineClasses(theme.layout.section, theme.layout.container)}>
        <div className="text-center mb-12">
          <h2
            className={combineClasses(
              "text-3xl font-bold mb-4",
              isLight ? "text-gray-900" : "text-white"
            )}
          >
            {getText('home.explore.title', lang)}
          </h2>
          <p className={isLight ? "text-gray-700" : "text-gray-400"}>
            {getText('home.explore.subtitle', lang)}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              title: getText('home.explore.cards.publications.title', lang),
              desc: getText('home.explore.cards.publications.desc', lang),
              page: getText('home.explore.cards.publications.page', lang),
              color: 'from-pink-500 to-rose-600',
              Icon: FileText
            },
            {
              title: getText('home.explore.cards.teaching.title', lang),
              desc: getText('home.explore.cards.teaching.desc', lang),
              page: getText('home.explore.cards.teaching.page', lang),
              color: 'from-purple-500 to-indigo-600',
              Icon: GraduationCap
            },
            {
              title: getText('home.explore.cards.news.title', lang),
              desc: getText('home.explore.cards.news.desc', lang),
              page: getText('home.explore.cards.news.page', lang),
              color: 'from-emerald-400 to-teal-600',
              Icon: Newspaper
            }
          ].map((item) => (
            <button
              key={item.page} // [key 修正] 使用稳定 key，避免 index key 带来的潜在重排问题
              onClick={() => onChangePage(item.page)}
              className={combineClasses(
                getCardStyle(themeMode),
                // p-6：卡片内部留白
                // text-left：文字左对齐（卡片信息密度更高，左对齐更利于扫描）
                // group：用于 hover 联动
                // hover:-translate-y-2：悬停上移
                // transform：启用变换（Tailwind 有些版本需要）
                // transition-all duration-300：平滑动画
                "p-6 text-left group hover:-translate-y-2 transform transition-all duration-300"
              )}
              aria-label={`${item.title} - ${item.desc}`} // [可访问性] 为卡片按钮提供语义描述
            >
              <div
                className={combineClasses(
                  // w-12 h-12：图标底板尺寸
                  // rounded-xl：圆角
                  // bg-gradient-to-r：渐变底板
                  // mb-4：与标题间距
                  // flex items-center justify-center：居中图标
                  "w-12 h-12 rounded-xl bg-gradient-to-r mb-4 flex items-center justify-center",
                  item.color
                )}
              >
                {/* [语义化图标] 每张卡片使用不同图标，提升扫描与识别效率 */}
                <item.Icon className="w-6 h-6 text-white" />
              </div>

              <h3
                className={combineClasses(
                  // text-xl font-bold：卡片标题层级
                  // mb-2：标题与描述间距
                  // transition-colors：hover 变色平滑
                  "text-xl font-bold mb-2 transition-colors",
                  isLight
                    ? "text-gray-900 group-hover:text-cyan-700"
                    : "text-white group-hover:text-cyan-300"
                )}
              >
                {item.title}
              </h3>

              <p className={combineClasses("text-sm", isLight ? "text-gray-700" : "text-gray-400")}>
                {item.desc}
              </p>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
};

export default HomeView;
